<!--
  ApprovalFilterChips.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Six-chip filter row above the queue. The active chip is highlighted;
  every chip shows the count of records currently in its bucket. The
  "Needs attention" chip is the default and combines the
  rejected / changes_requested / to_send / stale buckets per P2-Q2's
  precedence rule.

  Props:
    - modelValue: string -- current filter key (v-model).
    - counts: object -- the Pinia store's ``counts`` computed.

  Emits:
    - update:modelValue(key)

  The chips are rendered as native ``<button>`` elements so the page is
  keyboard-navigable without a Vuetify wrapper. Active styling and
  hover state come from local CSS.
-->
<template>
    <div class="aw-chips" role="tablist" data-testid="aw-filter-chips">
        <button
            v-for="chip in chipsWithCounts"
            :key="chip.key"
            type="button"
            role="tab"
            class="aw-chips__chip"
            :class="{
                'aw-chips__chip--active': chip.key === modelValue,
                'aw-chips__chip--attention': chip.key === 'needs_attention',
            }"
            :aria-selected="chip.key === modelValue ? 'true' : 'false'"
            :data-testid="`aw-filter-chip-${chip.key}`"
            @click="$emit('update:modelValue', chip.key)"
        >
            <span class="aw-chips__label">{{ chip.label }}</span>
            <span
                class="aw-chips__count"
                :data-testid="`aw-filter-chip-count-${chip.key}`"
                >{{ chip.count }}</span
            >
        </button>
    </div>
</template>

<script>
import { FILTER_CHIPS } from './filters'

export default {
    name: 'ApprovalFilterChips',
    props: {
        modelValue: {
            type: String,
            required: true,
        },
        counts: {
            type: Object,
            required: true,
        },
    },
    emits: ['update:modelValue'],
    computed: {
        chipsWithCounts() {
            return FILTER_CHIPS.map((chip) => ({
                ...chip,
                count: this.counts?.[chip.key] ?? 0,
            }))
        },
    },
}
</script>

<style scoped>
.aw-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}
.aw-chips__chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: 999px;
    background: var(--tf-bg, #ffffff);
    color: var(--tf-fg-2, #2d3748);
    font-size: 13px;
    cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease;
}
.aw-chips__chip:hover {
    background: var(--tf-surface-alt, #edf2f7);
}
.aw-chips__chip--active {
    background: var(--tf-primary-soft, rgba(25, 118, 210, 0.12));
    border-color: var(--tf-primary, #1976d2);
    color: var(--tf-primary-press, #0d47a1);
    font-weight: 500;
}
.aw-chips__chip--attention {
    border-color: #c97900;
}
.aw-chips__chip--attention.aw-chips__chip--active {
    background: #fff3df;
    border-color: #c97900;
    color: #7a4f00;
}
.aw-chips__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    padding: 0 6px;
    height: 18px;
    border-radius: 9px;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-3, #4a5568);
    font-size: 11px;
    font-weight: 600;
}
.aw-chips__chip--active .aw-chips__count {
    background: var(--tf-primary, #1976d2);
    color: #ffffff;
}
</style>
